import React from 'react';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ClientAcquisition } from '../../../types/utility.types';

interface ClientAcquisitionTimelineProps {
  data: ClientAcquisition[];
  isLoading: boolean;
}

const ClientAcquisitionTimeline: React.FC<ClientAcquisitionTimelineProps> = ({ data, isLoading }) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Client Acquisition Timeline
          </Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Format the period for display (YYYY-MM to Month YYYY)
  const formatPeriod = (period: string) => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('default', { month: 'short', year: 'numeric' });
  };

  // Sort data by period
  const chartData = [...data].sort((a, b) => {
    const periodA = a.period.split('-').map(Number);
    const periodB = b.period.split('-').map(Number);
    
    if (periodA[0] !== periodB[0]) {
      return periodA[0] - periodB[0];
    }
    return periodA[1] - periodB[1];
  }).map(item => ({
    ...item,
    formattedPeriod: formatPeriod(item.period)
  }));

  // Calculate the trend line (cumulative count)
  let cumulativeCount = 0;
  const chartDataWithTrend = chartData.map(item => {
    cumulativeCount += item.count;
    return {
      ...item,
      cumulative: cumulativeCount
    };
  });

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Client Acquisition Timeline
        </Typography>
        {chartData.length > 0 ? (
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartDataWithTrend}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="formattedPeriod" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip 
                  contentStyle={{
                    background: 'rgba(30, 41, 59, 0.95)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}
                  labelStyle={{
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="count"
                  name="New Clients"
                  stroke={theme.palette.primary.main}
                  activeDot={{ r: 8 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="cumulative"
                  name="Total Clients"
                  stroke={theme.palette.secondary.main}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>No data available</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientAcquisitionTimeline;
